/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EmployeePage.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-EMPLOYEES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EmployeePage - Frontend UI module: EmployeePage.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: заголовок в стиле портфеля, skeleton при загрузке.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блоки/логи для логики.
 * END_CHANGE_SUMMARY
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
} from '../../api/employeeApi';
import { getProjects } from '../../api/projectApi';
import { getProjectRoles } from '../../api/projectRoleApi';
import Employee from '../../models/Employee';
import Project from '../../models/Project';
import ProjectRole from '../../models/ProjectRole';
import EmployeeList from './components/EmployeeList';
import CreateEmployeeDialog from './components/CreateEmployeeDialog';
import EditEmployeeDialog from './components/EditEmployeeDialog';
import DeleteEmployeeDialog from './components/DeleteEmployeeDialog';

const displayFont = '"Syne", "Inter", sans-serif';

function EmployeeListSkeleton() {
  return (
    <Box sx={{ borderRadius: 1, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <Skeleton variant="rectangular" height={40} />
      {[0, 1, 2].map((key) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
          <Skeleton variant="circular" width={36} height={36} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="40%" height={20} />
            <Skeleton width="25%" height={16} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* START_METHOD_getAvailableRolesForCreate */
/* START_CONTRACT: getAvailableRolesForCreate
 * PURPOSE: Вернуть роли, ещё не назначенные ни одному сотруднику (для создания).
 * INPUTS: { roles: ProjectRole[], employees: Employee[] }.
 * OUTPUTS: { ProjectRole[] }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-EMPLOYEES
 * END_CONTRACT: getAvailableRolesForCreate
 */
function getAvailableRolesForCreate(roles, employees) {
  const assignedRoleIds = new Set(employees.map((e) => e.projectRoleId));
  return roles.filter((role) => !assignedRoleIds.has(role.id));
}
/* END_METHOD_getAvailableRolesForCreate */

/* START_METHOD_getAvailableRolesForEdit */
/* START_CONTRACT: getAvailableRolesForEdit
 * PURPOSE: Вернуть роли, свободные для редактируемого сотрудника (исключая занятые другими).
 * INPUTS: { roles: ProjectRole[], employees: Employee[], currentEmployeeId: number }.
 * OUTPUTS: { ProjectRole[] }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-EMPLOYEES
 * END_CONTRACT: getAvailableRolesForEdit
 */
function getAvailableRolesForEdit(roles, employees, currentEmployeeId) {
  const assignedRoleIds = new Set(
    employees
      .filter((e) => e.id !== currentEmployeeId)
      .map((e) => e.projectRoleId)
  );
  return roles.filter((role) => !assignedRoleIds.has(role.id));
}
/* END_METHOD_getAvailableRolesForEdit */

/* START_METHOD_EmployeePage */
/* START_CONTRACT: EmployeePage
 * PURPOSE: Страница сотрудников проекта: список, создание, редактирование, удаление с учётом свободных ролей.
 * INPUTS: {} (projectId из useParams).
 * OUTPUTS: { JSX.Element - страница с списком и диалогами }.
 * SIDE_EFFECTS: HTTP getProjects/getEmployees/getProjectRoles/create/update/deleteEmployee; навигация.
 * LINKS: M-FE-EMPLOYEES, M-FE-API
 * END_CONTRACT: EmployeePage
 */
export default function EmployeePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const numericProjectId = Number(projectId);

  const [project, setProject] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingEmployee, setDeletingEmployee] = useState(null);

  /* START_METHOD_fetchData */
  /* START_CONTRACT: fetchData
   * PURPOSE: Загрузить проект, сотрудников и роли; обработать отсутствие проекта и ошибки.
   * INPUTS: { signal?: AbortSignal }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP getProjects/getEmployees/getProjectRoles; setState.
   * LINKS: M-FE-EMPLOYEES
   * END_CONTRACT: fetchData
   */
  const fetchData = useCallback(async (signal) => {
    if (!numericProjectId) {
      /* START_BLOCK_INVALID_ID */
      setError('Некорректный идентификатор проекта');
      setLoading(false);
      return;
      /* END_BLOCK_INVALID_ID */
    }

    try {
      const [projectsRes, employeesRes, rolesRes] = await Promise.all([
        getProjects(signal),
        getEmployees(numericProjectId, signal),
        getProjectRoles(numericProjectId, signal),
      ]);

      const foundProject = projectsRes.data
        .map(Project.fromResponse)
        .find((p) => p.id === numericProjectId);

      if (!foundProject) {
        /* START_BLOCK_PROJECT_NOT_FOUND */
        setError('Проект не найден');
        return;
        /* END_BLOCK_PROJECT_NOT_FOUND */
      }

      /* START_BLOCK_LOAD_OK */
      setProject(foundProject);
      setEmployees(employeesRes.data.map(Employee.fromResponse));
      setRoles(rolesRes.data.map(ProjectRole.fromResponse));
      setError('');
      /* END_BLOCK_LOAD_OK */
    } catch (err) {
      /* START_BLOCK_LOAD_FAIL */
      if (signal?.aborted) return;
      console.warn('[EmployeePage][fetchData][LOAD_FAIL] employees load failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Ошибка загрузки сотрудников');
      /* END_BLOCK_LOAD_FAIL */
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [numericProjectId]);
  /* END_METHOD_fetchData */

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const refetch = () => fetchData();

  /* START_METHOD_handleCreate */
  /* START_CONTRACT: handleCreate
   * PURPOSE: Создать сотрудника в проекте и обновить данные.
   * INPUTS: { payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP createEmployee; refetch.
   * LINKS: M-FE-EMPLOYEES
   * END_CONTRACT: handleCreate
   */
  const handleCreate = async (payload) => {
    await createEmployee(numericProjectId, payload);
    await refetch();
  };
  /* END_METHOD_handleCreate */

  /* START_METHOD_handleUpdate */
  /* START_CONTRACT: handleUpdate
   * PURPOSE: Обновить сотрудника по id и обновить данные.
   * INPUTS: { id: number, payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP updateEmployee; refetch.
   * LINKS: M-FE-EMPLOYEES
   * END_CONTRACT: handleUpdate
   */
  const handleUpdate = async (id, payload) => {
    await updateEmployee(numericProjectId, id, payload);
    await refetch();
  };
  /* END_METHOD_handleUpdate */

  /* START_METHOD_handleDelete */
  /* START_CONTRACT: handleDelete
   * PURPOSE: Удалить сотрудника по id и обновить данные.
   * INPUTS: { id: number }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP deleteEmployee; refetch.
   * LINKS: M-FE-EMPLOYEES
   * END_CONTRACT: handleDelete
   */
  const handleDelete = async (id) => {
    await deleteEmployee(numericProjectId, id);
    await refetch();
  };
  /* END_METHOD_handleDelete */

  const createAvailableRoles = useMemo(
    () => getAvailableRolesForCreate(roles, employees),
    [roles, employees]
  );

  const editAvailableRoles = useMemo(
    () => (editingEmployee
      ? getAvailableRolesForEdit(roles, employees, editingEmployee.id)
      : []),
    [roles, employees, editingEmployee]
  );

  if (loading) {
    return (
      <Box>
        <Skeleton width={120} height={32} sx={{ mb: 2 }} />
        <Skeleton width="55%" height={40} sx={{ mb: 3 }} />
        <EmployeeListSkeleton />
      </Box>
    );
  }

  if (error && !project) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          К проектам
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        size="small"
        onClick={() => navigate('/projects')}
        sx={{ mb: 2 }}
      >
        К проектам
      </Button>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          pb: 3,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.12em',
              lineHeight: 1.4,
              mb: 0.5,
            }}
          >
            Команда
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontFamily: displayFont,
                fontWeight: 700,
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
              }}
            >
              {project?.name}
            </Typography>
            {employees.length > 0 && (
              <Chip
                label={`${employees.length} чел.`}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: (theme) => theme.palette.primary.main + '14',
                  color: 'primary.main',
                  border: 'none',
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 480 }}>
            Назначьте роли и загрузку — так планировщик сможет учитывать состав команды и отсутствия.
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          disabled={createAvailableRoles.length === 0}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
        >
          Новый сотрудник
        </Button>
      </Box>

      {createAvailableRoles.length === 0 && roles.length > 0 && employees.length >= roles.length && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Все проектные роли уже назначены сотрудникам
        </Alert>
      )}

      {roles.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Сначала создайте проектные роли для назначения сотрудникам
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <EmployeeList
        employees={employees}
        onEdit={(employee) => {
          setEditingEmployee(employee);
          setEditOpen(true);
        }}
        onDelete={(employee) => {
          setDeletingEmployee(employee);
          setDeleteOpen(true);
        }}
      />

      <CreateEmployeeDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        project={project}
        availableRoles={createAvailableRoles}
      />

      <EditEmployeeDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditingEmployee(null);
        }}
        onUpdate={handleUpdate}
        employee={editingEmployee}
        project={project}
        availableRoles={editAvailableRoles}
        allRoles={roles}
      />

      <DeleteEmployeeDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeletingEmployee(null);
        }}
        onConfirm={handleDelete}
        employee={deletingEmployee}
      />
    </Box>
  );
}
/* END_METHOD_EmployeePage */
